// Schéma des formulaires de démarche (brief L1-A) : sections, champs typés, propriétés, conditions
// d'affichage (visible_si, combinaisons ET et OU), calculs simples (somme, produit, référence à un
// tarif). Le même schéma sert à l'éditeur, au rendu et à la validation des données, côté serveur
// comme côté client (voir demarches-moteur-formulaire.ts).
import { z } from 'zod';

const codeElement = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/, 'Code en minuscules, chiffres et soulignés (63 caractères au plus)');
const codePiece = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de type de pièce invalide');
export const schemaDateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');

export const TYPES_CHAMP = [
  'texte_court',
  'texte_long',
  'nombre',
  'montant',
  'date',
  'choix_unique',
  'choix_multiple',
  'oui_non',
  'telephone',
  'courriel',
  'adresse',
  'localisation',
  'fichier',
  'tableau',
] as const;
export type TypeChamp = (typeof TYPES_CHAMP)[number];

/** Types admis pour les colonnes d'un tableau de lignes. */
export const TYPES_COLONNE = ['texte_court', 'nombre', 'montant', 'date', 'choix_unique', 'oui_non'] as const;
export type TypeColonne = (typeof TYPES_COLONNE)[number];

/** Types dont la valeur est numérique (opérandes et cibles de calcul). */
export const TYPES_NUMERIQUES: readonly TypeChamp[] = ['nombre', 'montant'];

export const LIBELLES_TYPES_CHAMP: Record<TypeChamp, string> = {
  texte_court: 'Texte court',
  texte_long: 'Texte long',
  nombre: 'Nombre',
  montant: 'Montant (francs CFA)',
  date: 'Date',
  choix_unique: 'Choix unique',
  choix_multiple: 'Choix multiple',
  oui_non: 'Oui ou non',
  telephone: 'Téléphone',
  courriel: 'Courriel',
  adresse: 'Adresse',
  localisation: 'Localisation sur la carte',
  fichier: 'Fichier',
  tableau: 'Tableau de lignes',
};

export const OPERATEURS_CONDITION = [
  'egal',
  'different',
  'superieur',
  'superieur_ou_egal',
  'inferieur',
  'inferieur_ou_egal',
  'contient',
  'renseigne',
  'non_renseigne',
] as const;
export type OperateurCondition = (typeof OPERATEURS_CONDITION)[number];

export type ValeurCondition = string | number | boolean | null;

/** Condition d'affichage : comparaison sur un champ, ou combinaison ET (et) ou OU (ou). */
export type Condition =
  | { champ: string; operateur: OperateurCondition; valeur?: ValeurCondition }
  | { et: Condition[] }
  | { ou: Condition[] };

export const schemaCondition: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.strictObject({
      champ: codeElement,
      operateur: z.enum(OPERATEURS_CONDITION),
      valeur: z.union([z.string().max(500), z.number(), z.boolean(), z.null()]).optional(),
    }),
    z.strictObject({ et: z.array(schemaCondition).min(1).max(20) }),
    z.strictObject({ ou: z.array(schemaCondition).min(1).max(20) }),
  ]),
);

/** Opérande d'un calcul : champ numérique, constante, tarif (rubrique) ou somme d'une colonne de tableau. */
export type Operande = { champ: string } | { constante: number } | { tarif: string } | { colonne: { tableau: string; colonne: string } };

export const schemaOperande: z.ZodType<Operande> = z.union([
  z.strictObject({ champ: codeElement }),
  z.strictObject({ constante: z.number().finite() }),
  z.strictObject({ tarif: codePiece }),
  z.strictObject({ colonne: z.strictObject({ tableau: codeElement, colonne: codeElement }) }),
]);

export const schemaCalcul = z.strictObject({
  operation: z.enum(['somme', 'produit']),
  operandes: z.array(schemaOperande).min(1).max(20),
});
export type Calcul = z.infer<typeof schemaCalcul>;

export const schemaValidationChamp = z
  .strictObject({
    /** Valeur minimale (nombre, montant), nombre minimal de choix ou de lignes. */
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    longueur_min: z.number().int().min(0).max(10000).optional(),
    longueur_max: z.number().int().min(1).max(10000).optional(),
    /** Expression régulière que la valeur texte doit respecter en entier. */
    motif: z.string().min(1).max(200).optional(),
    /** Message affiché si le motif n'est pas respecté. */
    message_motif: z.string().max(300).optional(),
    date_min: schemaDateCivile.optional(),
    date_max: schemaDateCivile.optional(),
  })
  .refine((v) => v.min === undefined || v.max === undefined || v.min <= v.max, { message: 'Le minimum dépasse le maximum', path: ['min'] })
  .refine((v) => v.longueur_min === undefined || v.longueur_max === undefined || v.longueur_min <= v.longueur_max, {
    message: 'La longueur minimale dépasse la longueur maximale',
    path: ['longueur_min'],
  })
  .refine((v) => !v.date_min || !v.date_max || v.date_min <= v.date_max, { message: 'La date minimale suit la date maximale', path: ['date_min'] });
export type ValidationChamp = z.infer<typeof schemaValidationChamp>;

export const schemaOption = z.strictObject({
  valeur: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,62}$/, 'Valeur d\'option en minuscules, chiffres, soulignés ou tirets'),
  libelle: z.string().trim().min(1).max(200),
});

export const schemaColonne = z.strictObject({
  code: codeElement,
  type: z.enum(TYPES_COLONNE),
  libelle: z.string().trim().min(1).max(200),
  obligatoire: z.boolean().default(false),
  options: z.array(schemaOption).max(100).optional(),
  validation: schemaValidationChamp.optional(),
});
export type Colonne = z.infer<typeof schemaColonne>;

export const schemaChamp = z.strictObject({
  code: codeElement,
  type: z.enum(TYPES_CHAMP),
  libelle: z.string().trim().min(1).max(200),
  aide: z.string().trim().max(1000).optional(),
  obligatoire: z.boolean().default(false),
  valeur_defaut: z.unknown().optional(),
  validation: schemaValidationChamp.optional(),
  /** Choix proposés (choix unique ou multiple). */
  options: z.array(schemaOption).max(100).optional(),
  /** Colonnes d'un tableau de lignes. */
  colonnes: z.array(schemaColonne).max(20).optional(),
  /** Type de pièce attendu pour un champ fichier (catalogue des types de pièces). */
  type_piece: codePiece.optional(),
  visible_si: schemaCondition.optional(),
  /** Champ calculé par le serveur (nombre ou montant) : toute valeur saisie est remplacée. */
  calcul: schemaCalcul.optional(),
});
export type Champ = z.infer<typeof schemaChamp>;

export const schemaSection = z.strictObject({
  code: codeElement,
  titre: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  visible_si: schemaCondition.optional(),
  champs: z.array(schemaChamp).min(1).max(100),
});
export type Section = z.infer<typeof schemaSection>;

/** Codes référencés par une condition (récursivement). */
export function champsDeCondition(c: Condition | undefined): string[] {
  if (!c) return [];
  if ('et' in c) return c.et.flatMap(champsDeCondition);
  if ('ou' in c) return c.ou.flatMap(champsDeCondition);
  return [c.champ];
}

/** Dépendances de visibilité d'un champ : sa condition et celle de sa section. */
export function dependancesVisibilite(section: Section, champ: Champ): string[] {
  return [...new Set([...champsDeCondition(section.visible_si), ...champsDeCondition(champ.visible_si)])];
}

/** Ordre topologique des codes selon un graphe de dépendances ; null en cas de cycle. */
export function ordreTopologique(deps: Map<string, string[]>): string[] | null {
  const ordre: string[] = [];
  const etat = new Map<string, 'encours' | 'fait'>();
  const visiter = (n: string): boolean => {
    const e = etat.get(n);
    if (e === 'fait') return true;
    if (e === 'encours') return false;
    etat.set(n, 'encours');
    for (const d of deps.get(n) ?? []) if (deps.has(d) && !visiter(d)) return false;
    etat.set(n, 'fait');
    ordre.push(n);
    return true;
  };
  for (const n of deps.keys()) if (!visiter(n)) return null;
  return ordre;
}

function verifierFormulaire(f: { sections: Section[] }, ctx: z.RefinementCtx): void {
  const champs = new Map<string, Champ>();
  const sections = new Set<string>();
  f.sections.forEach((s, i) => {
    if (sections.has(s.code)) ctx.addIssue({ code: 'custom', message: `Section en double : ${s.code}`, path: ['sections', i, 'code'] });
    sections.add(s.code);
    s.champs.forEach((c, j) => {
      if (champs.has(c.code)) ctx.addIssue({ code: 'custom', message: `Champ en double : ${c.code}`, path: ['sections', i, 'champs', j, 'code'] });
      champs.set(c.code, c);
    });
  });
  const visibilite = new Map<string, string[]>();
  const calculs = new Map<string, string[]>();
  f.sections.forEach((s, i) => {
    s.champs.forEach((c, j) => {
      const chemin = ['sections', i, 'champs', j];
      const ajouter = (message: string, suite: (string | number)[] = []) => ctx.addIssue({ code: 'custom', message, path: [...chemin, ...suite] });
      const avecOptions = c.type === 'choix_unique' || c.type === 'choix_multiple';
      if (avecOptions && !(c.options && c.options.length > 0)) ajouter('Un champ à choix propose au moins une option', ['options']);
      if (!avecOptions && c.options) ajouter('Seuls les champs à choix ont des options', ['options']);
      if (c.options && new Set(c.options.map((o) => o.valeur)).size !== c.options.length) ajouter('Options en double', ['options']);
      if (c.type === 'tableau') {
        if (!c.colonnes || c.colonnes.length === 0) ajouter('Un tableau comporte au moins une colonne', ['colonnes']);
        else {
          if (new Set(c.colonnes.map((k) => k.code)).size !== c.colonnes.length) ajouter('Colonnes en double', ['colonnes']);
          c.colonnes.forEach((k, n) => {
            if (k.type === 'choix_unique' && !(k.options && k.options.length > 0)) ajouter('Une colonne à choix propose au moins une option', ['colonnes', n, 'options']);
          });
        }
      } else if (c.colonnes) ajouter('Seuls les tableaux ont des colonnes', ['colonnes']);
      if (c.type_piece && c.type !== 'fichier') ajouter('Seuls les champs fichier précisent un type de pièce', ['type_piece']);
      const motif = c.validation?.motif;
      if (motif) {
        try {
          new RegExp(motif);
        } catch {
          ajouter('Motif de validation invalide', ['validation', 'motif']);
        }
      }
      for (const ref of champsDeCondition(c.visible_si)) {
        if (!champs.has(ref)) ajouter(`Condition sur un champ inconnu : ${ref}`, ['visible_si']);
        if (ref === c.code) ajouter('Un champ ne peut pas dépendre de lui-même', ['visible_si']);
      }
      visibilite.set(c.code, dependancesVisibilite(s, c).filter((r) => champs.has(r)));
      if (c.calcul) {
        if (!TYPES_NUMERIQUES.includes(c.type)) ajouter('Seuls les champs nombre et montant peuvent être calculés', ['calcul']);
        const refs: string[] = [];
        c.calcul.operandes.forEach((o, n) => {
          if ('champ' in o) {
            const cible = champs.get(o.champ);
            if (!cible || !TYPES_NUMERIQUES.includes(cible.type)) ajouter(`Opérande non numérique ou inconnu : ${o.champ}`, ['calcul', 'operandes', n]);
            else refs.push(o.champ);
          }
          if ('colonne' in o) {
            const t = champs.get(o.colonne.tableau);
            const k = t?.colonnes?.find((x) => x.code === o.colonne.colonne);
            if (!t || t.type !== 'tableau' || !k || (k.type !== 'nombre' && k.type !== 'montant')) {
              ajouter(`Colonne numérique inconnue : ${o.colonne.tableau}.${o.colonne.colonne}`, ['calcul', 'operandes', n]);
            }
          }
        });
        calculs.set(c.code, refs);
      }
    });
    for (const ref of champsDeCondition(s.visible_si)) {
      if (!champs.has(ref)) ctx.addIssue({ code: 'custom', message: `Condition sur un champ inconnu : ${ref}`, path: ['sections', i, 'visible_si'] });
      if (s.champs.some((c) => c.code === ref)) ctx.addIssue({ code: 'custom', message: 'Une section ne peut pas dépendre de ses propres champs', path: ['sections', i, 'visible_si'] });
    }
  });
  if (ordreTopologique(visibilite) === null) ctx.addIssue({ code: 'custom', message: 'Les conditions d\'affichage forment un cycle', path: ['sections'] });
  if (ordreTopologique(calculs) === null) ctx.addIssue({ code: 'custom', message: 'Les calculs forment un cycle', path: ['sections'] });
}

export const schemaFormulaire = z
  .strictObject({ sections: z.array(schemaSection).max(30).default([]) })
  .superRefine(verifierFormulaire);
export type Formulaire = z.infer<typeof schemaFormulaire>;

/** Ensemble des champs d'un formulaire avec leur section. */
export function tousLesChamps(f: Formulaire): { section: Section; champ: Champ }[] {
  return f.sections.flatMap((section) => section.champs.map((champ) => ({ section, champ })));
}
